import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { api, getCountries } from "ticketino-api-client";
import axios from "axios";

import Header from './components/Header'
import Footer from "./components/Footer";
import Loading from "./components/Loading";

const Participant = () => {
    const [posId, setPosId] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [resources, setResources] = useState({});
    const [orderId, setOrderId] = useState("");
    const [agbAccepted, setAgbAccepted] = useState(false);
    const [token, setToken] = useState(sessionStorage.getItem("token"));
    const [errors, setErrors] = useState("");
    const [registrationOpen, setRegistrationOpen] = useState(true);

    const { language, companionNumber } = useParams();
    // the URL is the source of truth for WHICH person is on this page - the main
    // participant has no companion segment (index 0), each companion has their own
    // /companion/:companionNumber segment, so browser back/forward always lands on
    // the right person instead of whatever "buyerInfo" happens to hold at that moment
    const activeCompanionIndex = companionNumber ? parseInt(companionNumber, 10) : 0;
    const isCompanion = activeCompanionIndex > 0;
    const buyerInfoKey = activeCompanionIndex === 0 ? "mainBuyerInfo" : `companionBuyerInfo_${activeCompanionIndex}`;

    const [buyerInfo, setBuyerInfo] = useState(() => {
        const defaults = {
            salutation: "",
            firstName: "",
            name: "",
            company: "",
            function: "",
            telephone: "",
            email: "",
            street: "",
            streetNumber: "",
            city: "",
            postCode: "",
            countryId: 176, // Default to Switzerland
            anreise: "",
        };
        // pre-fill with whatever this specific person (identified by the URL) had
        // already entered, when navigating back here
        try {
            const saved = JSON.parse(sessionStorage.getItem(buyerInfoKey) || "null");
            return saved ? { ...defaults, ...saved } : defaults;
        } catch {
            return defaults;
        }
    });

    const [countries, setCountries] = useState([]);

    const navigate = useNavigate();
    const baseUrl = process.env.REACT_APP_BASEURL_API;
    let languageId = 0;

    // changing languageId according to the url
    switch (language) {
        case ("de" || "DE"):
            languageId = 1;
            break;
        case ("fr" || "FR"):
            languageId = 2;
            break;
        default:
            languageId = 0;
            break;
    }

    api.defaults.baseURL = baseUrl;

    useEffect(() => {
        loadToken();
    }, []);

    const loadToken = () => {
        axios.get("form/token").then((res) => {
            axios.defaults.headers.common["Authorization"] = "Bearer " + res.data;
            api.defaults.headers.common["Authorization"] = "Bearer " + res.data;
            setToken(res.data);
            sessionStorage.setItem("token", res.data);
            requestFormSettings();
            loadCountries();
        });
    };

    const loadCountries = async () => {
        try {
            let fetchedCountries = await getCountries(language);

            const indexCH = fetchedCountries.findIndex(c => c.id === 176);
            const ch = fetchedCountries[indexCH];
            fetchedCountries.splice(indexCH, 1);

            const indexDE = fetchedCountries.findIndex(c => c.id === 229);
            const de = fetchedCountries[indexDE];
            fetchedCountries.splice(indexDE, 1);

            const indexAT = fetchedCountries.findIndex(c => c.id === 228);
            const at = fetchedCountries[indexAT];
            fetchedCountries.splice(indexAT, 1);

            fetchedCountries.unshift({ id: 0, name: "---------" });
            fetchedCountries.unshift(at);
            fetchedCountries.unshift(de);
            fetchedCountries.unshift(ch);

            setCountries(fetchedCountries);
        } catch (error) {
            console.error(error);
        }
    };

    const requestFormSettings = async () => {
        await axios.get(`form/formsettings`).then((res) => {
            const serverDate = new Date(res.headers['date']);
            const deadline = new Date(res.data.registrationDeadline);
            setRegistrationOpen(serverDate <= deadline);
            setPosId(res.data.posId);
            if (!sessionStorage.getItem("orderId")) {
                startOrder(res.data.posId);
            }
        });
    };

    const startOrder = (posId) => {
        let order = {
            affiliateShopId: null,
            currency: "CHF",
            tenantId: 1,
            pointOfSaleId: posId,
            languageId: languageId,
            abbreviation: "",
            paymentType: 1,
            deliveryType: 1,
        };

        axios
            .post(`${baseUrl}/ShopBasket/Order`, order)
            .then((res) => {
                sessionStorage.setItem("orderId", res.data.id);
                setOrderId(res.data.id);
            })
            .catch((error) => console.error(error.response.data));
    };

    const handleLanguageChange = (lang) => {
        navigate(isCompanion ? `/${lang}/participant/companion/${activeCompanionIndex}` : `/${lang}/participant`);
    };

    const handleBack = () => {
        const mainBuyerInfo = sessionStorage.getItem("mainBuyerInfo");
        if (mainBuyerInfo) {
            sessionStorage.setItem("buyerInfo", mainBuyerInfo);
        }
        sessionStorage.setItem("companionCount", String(Math.max(activeCompanionIndex - 1, 0)));
        navigate(`/${language}/program`);
    };

    const requestResources = async () => {
        await axios
            .get(`form/resources/${language}`)
            .then((res) => {
                setResources(res.data?.translation);
            })
            .catch((error) => console.error(error.response.data));
    };

    useEffect(() => {
        requestResources();
        if (token) {
            loadCountries();
        }
    }, [language]);

    const onInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setBuyerInfo((prevBuyerInfo) => ({
            ...prevBuyerInfo,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const validateEntries = () => {
        if (!(buyerInfo.salutation && buyerInfo.salutation.trim() !== "") ||
            !(buyerInfo.firstName && /^.{1,70}$/.test(buyerInfo.firstName)) ||
            !(buyerInfo.name && /^.{1,70}$/.test(buyerInfo.name)) ||
            !(buyerInfo.company && /^.{1,70}$/.test(buyerInfo.company)) ||
            !(buyerInfo.street && buyerInfo.street.trim() !== "") ||
            !(buyerInfo.streetNumber && buyerInfo.streetNumber.trim() !== "") ||
            !(buyerInfo.postCode && buyerInfo.postCode.trim() !== "") ||
            !(buyerInfo.city && buyerInfo.city.trim() !== "") ||
            !(buyerInfo.countryId > 0)) {
            setErrors(resources?.FillMandatoryFields);
            return false;
        }
        else if (!(buyerInfo.email && /^\S+@\S+\.\S+$/.test(buyerInfo.email))) {
            setErrors(resources?.EmailNotCorrect);
            return false;
        }
        setErrors("");
        return true;
    }

    const addAddressToBasket = async () => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ 'event': 'addAddress' });
        const toSave = {
            ...buyerInfo,
            countryName: countries.find(c => c.id === parseInt(buyerInfo.countryId))?.name ?? "",
            isRailway: buyerInfo.anreise === resources?.BuyerAnreiseValue1,
        };
        sessionStorage.setItem("buyerInfo", JSON.stringify(toSave));
        sessionStorage.setItem(buyerInfoKey, JSON.stringify(toSave));
        navigate(`/${language}/program`);
    };

    const handleRegister = async () => {
        if (isSubmitting) return;

        if (validateEntries()) {
            setIsSubmitting(true);
            try {
                await addAddressToBasket();
            } catch (error) {
                console.error("Error during registration:", error);
                setErrors(resources?.ErrorOccurred);
                setIsSubmitting(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    return (
        <div>
            <Header resources={resources} title={resources?.HomeTitle} language={language} onLanguageChange={handleLanguageChange}
                containerClass="page-container-smaller" />
            {loading ? (
                <Loading
                    alignment="center"
                    color="#d3d3d3"
                    bgColor="#fff"
                    top="50%"
                />
            ) : (
                <>
                    <div className="container page-container">

                        <div className="mt-4 page-container-smaller">

                            {!registrationOpen ? (
                                <Navigate to="/404" replace />
                            ) : (
                                <>
                                    {!isCompanion && (
                                        <div className="mt-4 mb-2">
                                            <strong><p dangerouslySetInnerHTML={{ __html: resources?.HomeRegistrationCloseDate }}></p></strong>
                                        </div>
                                    )}

                                    {errors !== "" && (
                                        <div className="alert alert-danger" role="alert">
                                            {errors}
                                        </div>
                                    )}

                                    {/* Buyer Information Form Section */}

                                    <h5 className="subtitle mt-4 mb-4">{isCompanion ? resources?.HomeTitleDescriptionCompanion : resources?.HomeTitleDescription}</h5>

                                    {/* Salutation */}
                                    <div className="mb-4">
                                        <label className="form-label text-buyer-input" htmlFor="salutation">
                                            {resources?.BuyerSalutation} <span className="mandatory">*</span>
                                        </label>
                                        <select
                                            name="salutation"
                                            id="salutation"
                                            className="form-select"
                                            value={buyerInfo.salutation}
                                            onChange={onInputChange}
                                        >
                                            <option value="">{resources?.BuyerSalutationChoose}</option>
                                            <option value={resources?.Mr}>{resources?.Mr}</option>
                                            <option value={resources?.Ms}>{resources?.Ms}</option>
                                            <option value={resources?.Diverse}>{resources?.Diverse}</option>
                                        </select>
                                    </div>

                                    {/* Last Name */}
                                    <div className="mb-4">
                                        <label className="form-label text-buyer-input" htmlFor="name">
                                            {resources?.BuyerLastname} <span className="mandatory">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            className="form-control"
                                            value={buyerInfo.name}
                                            id="name"
                                            onChange={onInputChange}
                                        />
                                    </div>

                                    {/* First Name */}
                                    <div className="mb-4">
                                        <label className="form-label text-buyer-input" htmlFor="firstName">
                                            {resources?.BuyerFirstname} <span className="mandatory">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="firstName"
                                            className="form-control"
                                            value={buyerInfo.firstName}
                                            id="firstName"
                                            onChange={onInputChange}
                                        />
                                    </div>

                                    {/* Verein */}
                                    <div className="mb-4">
                                        <label className="form-label text-buyer-input" htmlFor="company">
                                            {resources?.BuyerCompany} <span className="mandatory">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="company"
                                            className="form-control"
                                            value={buyerInfo.company}
                                            id="company"
                                            onChange={onInputChange}
                                        />
                                    </div>

                                    {/* Vereins-Homepage */}
                                    <div className="mb-4">
                                        <label className="form-label text-buyer-input" htmlFor="function">
                                            {resources?.BuyerFunction} <span className="mandatory">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="function"
                                            className="form-control"
                                            value={buyerInfo.function}
                                            id="function"
                                            onChange={onInputChange}
                                        />
                                    </div>

                                    {/* Street + Number */}
                                    <div className="mb-4 row g-2">
                                        <div className="col-8">
                                            <label className="form-label text-buyer-input" htmlFor="street">
                                                {resources?.BuyerStreet} <span className="mandatory">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="street"
                                                className="form-control"
                                                value={buyerInfo.street}
                                                id="street"
                                                onChange={onInputChange}
                                            />
                                        </div>
                                        <div className="col-4">
                                            <label className="form-label text-buyer-input" htmlFor="streetNumber">
                                                {resources?.BuyerStreetNumber} <span className="mandatory">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="streetNumber"
                                                className="form-control"
                                                value={buyerInfo.streetNumber}
                                                id="streetNumber"
                                                onChange={onInputChange}
                                            />
                                        </div>
                                    </div>

                                    {/* Postcode + City */}
                                    <div className="mb-4 row g-2">
                                        <div className="col-4">
                                            <label className="form-label text-buyer-input" htmlFor="postCode">
                                                {resources?.BuyerPostcode} <span className="mandatory">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="postCode"
                                                className="form-control"
                                                value={buyerInfo.postCode}
                                                id="postCode"
                                                onChange={onInputChange}
                                            />
                                        </div>
                                        <div className="col-8">
                                            <label className="form-label text-buyer-input" htmlFor="city">
                                                {resources?.BuyerCity} <span className="mandatory">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="city"
                                                className="form-control"
                                                value={buyerInfo.city}
                                                id="city"
                                                onChange={onInputChange}
                                            />
                                        </div>
                                    </div>

                                    {/* Country */}
                                    <div className="mb-4">
                                        <label className="form-label text-buyer-input" htmlFor="countryId">
                                            {resources?.BuyerCountry} <span className="mandatory">*</span>
                                        </label>
                                        <select
                                            className="form-select"
                                            id="countryId"
                                            name="countryId"
                                            value={buyerInfo.countryId}
                                            onChange={onInputChange}
                                        >
                                            {countries.map((country) => (
                                                <option key={country.id} value={country.id}>{country.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Email */}
                                    <div className="mb-4">
                                        <label className="form-label text-buyer-input" htmlFor="email">
                                            {resources?.BuyerEmail} <span className="mandatory">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            className="form-control"
                                            value={buyerInfo.email}
                                            id="email"
                                            onChange={onInputChange}
                                        />
                                    </div>

                                    {/* Phone */}
                                    <div className="mb-4">
                                        <label className="form-label text-buyer-input" htmlFor="telephone">
                                            {resources?.BuyerPhone}
                                        </label>
                                        <input
                                            type="text"
                                            name="telephone"
                                            className="form-control"
                                            value={buyerInfo.telephone}
                                            id="telephone"
                                            onChange={onInputChange}
                                        />
                                    </div>

                                    {/* Anreise */}
                                    <div className="mb-4 mt-4">
                                        <label className="form-label text-buyer-input">
                                            <strong>{resources?.BuyerAnreise}</strong>
                                        </label>
                                        <div className="mt-2">
                                            <div className="form-check">
                                                <input
                                                    type="radio"
                                                    name="anreise"
                                                    id="anreise-rail"
                                                    className="form-check-input"
                                                    value={resources?.BuyerAnreiseValue1}
                                                    checked={buyerInfo.anreise === resources?.BuyerAnreiseValue1}
                                                    onChange={onInputChange}
                                                />
                                                <label className="form-check-label" htmlFor="anreise-rail">
                                                    {resources?.BuyerAnreiseValue1}
                                                </label>
                                            </div>
                                            <div className="form-check mt-2">
                                                <input
                                                    type="radio"
                                                    name="anreise"
                                                    id="anreise-pw"
                                                    className="form-check-input"
                                                    value={resources?.BuyerAnreiseValue2}
                                                    checked={buyerInfo.anreise === resources?.BuyerAnreiseValue2}
                                                    onChange={onInputChange}
                                                />
                                                <label className="form-check-label" htmlFor="anreise-pw">
                                                    {resources?.BuyerAnreiseValue2}
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-5">
                                        <div className="col-12">
                                            <span className="mandatory">*</span> <span>{resources?.BuyerMandatoryFields}</span>
                                        </div>
                                    </div>

                                    <div className="row mt-4">
                                        <div className="col-12 d-flex justify-content-between">
                                            {isCompanion && (
                                                <button
                                                    className="btn btn-custom"
                                                    onClick={handleBack}
                                                    disabled={isSubmitting}
                                                >
                                                    {resources?._Back}
                                                </button>
                                            )}
                                            <button
                                                className={`btn btn-custom ${isCompanion ? "" : "ms-auto"}`}
                                                onClick={handleRegister}
                                                disabled={isSubmitting}
                                            >
                                                {resources?._Next}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    <Footer language={language} />
                </>
            )}
        </div>
    )
}

export default Participant